import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Link,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useApi } from '@shared/services/api';

/** A company in TheOrg's directory. */
interface TheOrgCompany {
  domains: string[];
  id: string;
  logoUrl?: string | null;
  name: string;
  orgChartUrl?: string | null;
}

/** The stored mapping, as the API returns it. */
interface TheOrgMapping {
  checkedAt: string;
  company?: TheOrgCompany;
  fetchDomain?: string;
  lastFetch?: { domain: string; nodeCount: number; personCount: number; startedAt: string };
  resolvedBy: 'admin' | 'auto';
  searchedDomains?: string[];
  status: 'disabled' | 'mapped' | 'not_found';
}

interface TheOrgMappingPanelProps {
  companyId: number;
  companyUrl?: string;
}

/**
 * The bare host of a stored company URL.
 * @param url - A URL or bare domain.
 */
function hostOf(url?: string): string {
  if (!url) return '';
  try {
    return new URL(url.startsWith('http') ? url : `https://${url}`).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

const formatDate = (iso: string): string => new Date(iso).toLocaleString();

/**
 * Which TheOrg company this company's org chart comes from, and a way to
 * correct it.
 *
 * Discovery maps a company automatically the first time it runs, by searching
 * TheOrg for a company that owns one of its domains, and posts the result —
 * with how many people the chart brought in — to Slack. This panel shows that
 * mapping and lets an admin pin a different TheOrg company, say none fits, or
 * hand the decision back to automatic mapping. Searching TheOrg is free;
 * refreshing a chart is not, and posts its counts to Slack when done.
 */
const TheOrgMappingPanel: React.FC<TheOrgMappingPanelProps> = ({ companyId, companyUrl }) => {
  const api = useApi();
  const [mapping, setMapping] = useState<TheOrgMapping | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [query, setQuery] = useState(hostOf(companyUrl));
  const [results, setResults] = useState<TheOrgCompany[] | null>(null);
  const [selected, setSelected] = useState<TheOrgCompany | null>(null);
  const [fetchDomain, setFetchDomain] = useState('');
  const [busy, setBusy] = useState<null | 'auto' | 'disable' | 'map' | 'refresh' | 'search'>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    const response = await api.get(`/admin/company/${companyId}/theorg`);
    setIsLoading(false);
    if (response?.error) {
      setError(response.message ?? 'Could not load the TheOrg mapping');
      return;
    }
    setMapping(response?.mapping ?? null);
  }, [api, companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const search = async (): Promise<void> => {
    if (!query.trim()) return;
    setBusy('search');
    setError(null);
    setNotice(null);
    setSelected(null);
    const response = await api.post(`/admin/company/${companyId}/theorg-search`, { query: query.trim() });
    setBusy(null);
    if (response?.error) {
      setResults(null);
      setError(response.message ?? 'TheOrg search failed');
      return;
    }
    setResults(response?.results ?? []);
  };

  const choose = (company: TheOrgCompany): void => {
    setSelected(company);
    const ours = hostOf(companyUrl);
    setFetchDomain(company.domains.includes(ours) ? ours : (company.domains[0] ?? ''));
  };

  const save = async (body: Record<string, unknown>, action: 'auto' | 'disable' | 'map', success: string): Promise<void> => {
    setBusy(action);
    setError(null);
    setNotice(null);
    const response = await api.put(`/admin/company/${companyId}/theorg`, body);
    setBusy(null);
    if (response?.error) {
      setError(response.message ?? 'Could not save the TheOrg mapping');
      return;
    }
    setMapping(response?.mapping ?? null);
    setResults(null);
    setSelected(null);
    setNotice(success);
  };

  const refresh = async (): Promise<void> => {
    setBusy('refresh');
    setError(null);
    setNotice(null);
    const response = await api.post(`/admin/company/${companyId}/theorg-refresh`);
    setBusy(null);
    if (response?.error) {
      setError(response.message ?? 'Could not start the refresh');
      return;
    }
    setNotice('Refresh started. A large org chart takes a few minutes; the counts are posted to Slack when it finishes.');
  };

  const renderMapping = (): React.ReactNode => {
    if (isLoading) return <CircularProgress size={24} />;

    if (!mapping) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          Not mapped yet. Contact discovery maps it automatically the first time it runs for this company.
        </Typography>
      );
    }

    const how = mapping.resolvedBy === 'admin' ? 'Set by an admin' : 'Found automatically';

    if (mapping.status === 'disabled') {
      return (
        <Alert severity="warning">
          No TheOrg company fits — set by an admin {formatDate(mapping.checkedAt)}. Contact discovery uses Coresignal
          only.
        </Alert>
      );
    }

    if (mapping.status === 'not_found') {
      return (
        <Alert severity="error">
          Not found on TheOrg — searched {(mapping.searchedDomains ?? []).join(', ') || 'no domains'} on{' '}
          {formatDate(mapping.checkedAt)}. Searched again weekly; pin a company below if TheOrg files it under
          another domain.
        </Alert>
      );
    }

    const { company, lastFetch } = mapping;
    return (
      <Card variant="outlined" sx={{ backgroundColor: 'grey.50' }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {company?.orgChartUrl ? (
                <Link href={company.orgChartUrl} target="_blank" rel="noopener noreferrer">
                  {company.name}
                </Link>
              ) : (
                (company?.name ?? 'Unknown company')
              )}
            </Typography>
            <Chip label={how} size="small" color={mapping.resolvedBy === 'admin' ? 'primary' : 'default'} />
          </Box>
          <Box sx={{ columnGap: 2, display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Chart fetched by:
            </Typography>
            <Typography variant="body2">{mapping.fetchDomain}</Typography>

            <Typography variant="body2" color="text.secondary">
              TheOrg domains:
            </Typography>
            <Typography variant="body2">{company?.domains.join(', ') || 'N/A'}</Typography>

            <Typography variant="body2" color="text.secondary">
              Last fetch:
            </Typography>
            <Typography variant="body2">
              {lastFetch
                ? `${formatDate(lastFetch.startedAt)} — ${lastFetch.personCount.toLocaleString()} people`
                : 'Not fetched yet'}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
      <Typography variant="h6">TheOrg Org Chart</Typography>

      {error && <Alert severity="error">{error}</Alert>}
      {notice && <Alert severity="success">{notice}</Alert>}

      {renderMapping()}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {mapping?.status === 'mapped' && (
          <Button variant="outlined" onClick={refresh} disabled={busy != null}>
            {busy === 'refresh' ? <CircularProgress size={20} /> : 'Refresh org chart now'}
          </Button>
        )}
        {mapping?.resolvedBy === 'admin' && (
          <Button
            variant="text"
            onClick={() => save({ action: 'auto' }, 'auto', 'Cleared — discovery will map this company automatically.')}
            disabled={busy != null}
          >
            {busy === 'auto' ? <CircularProgress size={20} /> : 'Use automatic mapping'}
          </Button>
        )}
        {mapping?.status !== 'disabled' && (
          <Button
            variant="text"
            color="warning"
            onClick={() => save({ action: 'disable' }, 'disable', 'TheOrg disabled for this company.')}
            disabled={busy != null}
          >
            {busy === 'disable' ? <CircularProgress size={20} /> : 'No TheOrg company fits'}
          </Button>
        )}
      </Box>

      <Divider />

      <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
        <TextField
          size="small"
          label="Search TheOrg by name or domain"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') search();
          }}
          sx={{ flex: 1 }}
        />
        <Button variant="outlined" onClick={search} disabled={busy != null || !query.trim()}>
          {busy === 'search' ? <CircularProgress size={20} /> : 'Search TheOrg'}
        </Button>
      </Box>

      {results != null && results.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No TheOrg companies found.
        </Typography>
      )}

      {results != null && results.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {results.map((company) => {
            const isSelected = selected?.id === company.id;
            const isCurrent = mapping?.company?.id === company.id;
            return (
              <Card
                key={company.id}
                variant="outlined"
                sx={{
                  backgroundColor: isCurrent ? 'action.selected' : undefined,
                  border: isSelected ? '2px solid' : '1px solid',
                  borderColor: isSelected ? 'primary.main' : 'divider',
                }}
              >
                <CardActionArea onClick={() => choose(company)} aria-label={`Select ${company.name}`}>
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, justifyContent: 'space-between' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {company.name}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {isCurrent && <Chip label="Current" size="small" color="success" />}
                        {isSelected && <Chip label="Selected" size="small" color="primary" />}
                      </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {company.domains.join(', ') || 'No domains'}
                    </Typography>
                  </CardContent>
                </CardActionArea>
                {company.orgChartUrl && (
                  <Box sx={{ pb: 1, px: 2 }}>
                    <Link href={company.orgChartUrl} target="_blank" rel="noopener noreferrer" variant="body2">
                      View org chart on TheOrg
                    </Link>
                  </Box>
                )}
              </Card>
            );
          })}

          {selected && (
            <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <TextField
                select
                size="small"
                label="Fetch the chart by"
                value={fetchDomain}
                onChange={(event) => setFetchDomain(event.target.value)}
                sx={{ minWidth: 220 }}
              >
                {selected.domains.map((domain) => (
                  <MenuItem key={domain} value={domain}>
                    {domain}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                variant="contained"
                disabled={busy != null || !fetchDomain}
                onClick={() =>
                  save(
                    { action: 'map', fetchDomain, theOrgCompanyId: selected.id },
                    'map',
                    `Mapped to ${selected.name}. The next contact discovery — or "Refresh org chart now" — imports its chart.`,
                  )
                }
              >
                {busy === 'map' ? <CircularProgress size={20} /> : 'Use this company'}
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default TheOrgMappingPanel;
